import { 
  doc, 
  collection,
  setDoc, 
  getDoc,
  serverTimestamp,
  runTransaction
} from 'firebase/firestore';
import { db, collections } from '@/lib/firebase';
import { toast } from 'react-hot-toast';

export interface AssessmentData {
  userId: string;
  responses: Record<string, any>;
  completedAt: Date;
  status: 'completed' | 'in_progress';
}

export async function saveSelfAssessment(userId: string, data: Record<string, any>): Promise<void> {
  if (!userId) throw new Error('User ID is required');

  try {
    const userRef = doc(db, collections.users, userId);
    const assessmentRef = doc(collection(userRef, 'assessments'), 'self');

    // Use a transaction to ensure atomicity
    await runTransaction(db, async (transaction) => {
      const userDoc = await transaction.get(userRef);
      if (!userDoc.exists()) {
        throw new Error('User not found');
      }

      // Create the assessment document
      const assessmentData = {
        userId,
        responses: data,
        completedAt: serverTimestamp(),
        status: 'completed',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      transaction.set(assessmentRef, assessmentData);
      
      // Update user document
      transaction.update(userRef, {
        selfAssessmentCompleted: true,
        selfAssessmentCompletedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
    });
  } catch (error: any) {
    console.error('Error saving assessment:', error);
    throw new Error(error.message || 'Failed to save assessment');
  }
}

export async function saveDISCAssessment(userId: string, data: Record<string, any>): Promise<void> {
  if (!userId) throw new Error('User ID is required');

  try {
    const userRef = doc(db, collections.users, userId);
    const userDoc = await getDoc(userRef);

    if (!userDoc.exists()) {
      throw new Error('User not found');
    }
    
    await runTransaction(db, async (transaction) => {
      const userDoc = await transaction.get(userRef);
      if (!userDoc.exists()) {
        throw new Error('User not found');
      }

      // Update user document to mark DISC assessment as completed
      transaction.update(userRef, {
        discAssessmentCompleted: true,
        updatedAt: serverTimestamp()
      });

      // Store DISC assessment data in a subcollection
      const discRef = doc(collection(userRef, 'assessments'), 'disc');
      transaction.set(discRef, {
        responses: data,
        completedAt: serverTimestamp(),
        status: 'completed',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
    });

    toast.success('DISC Assessment completed successfully');
  } catch (error: any) {
    console.error('Error saving DISC assessment:', error);
    throw new Error('Failed to save DISC assessment. Please try again.');
  }
}

export async function getSelfAssessment(userId: string): Promise<AssessmentData | null> {
  if (!userId) throw new Error('User ID is required');

  try {
    const userRef = doc(db, collections.users, userId);
    const assessmentRef = doc(collection(userRef, 'assessments'), 'self');
    const assessmentDoc = await getDoc(assessmentRef);

    if (!assessmentDoc.exists()) {
      return null;
    }

    return assessmentDoc.data() as AssessmentData;
  } catch (error: any) {
    console.error('Error fetching assessment:', error);
    throw new Error('Failed to load assessment');
  }
}